import { Crown, Star, AlertTriangle, ExternalLink } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CandidateStatBar } from '@/components/candidate/CandidateStatBar'
import { IdeologyAlignmentBadge } from '@/components/ideology/IdeologyAlignmentBadge'
import type { Candidate, IdeologyVector } from '@/types'
import { cn } from '@/lib/utils'

interface PartyLeaderCardProps {
  leader: Candidate
  partyIdeology: IdeologyVector
  onViewDetails: () => void
  className?: string
}

function getBestSkill(candidate: Candidate): { name: string; value: number } {
  const skills = [
    { name: 'Charisma', value: candidate.charisma },
    { name: 'Admin', value: candidate.admin_skill },
    { name: 'Debate', value: candidate.debate_skill },
  ]
  return skills.reduce((a, b) => (a.value > b.value ? a : b))
}

export function PartyLeaderCard({
  leader,
  partyIdeology,
  onViewDetails,
  className,
}: PartyLeaderCardProps) {
  const isLowLoyalty = leader.loyalty < 40
  const bestSkill = getBestSkill(leader)

  return (
    <Card
      className={cn(
        'border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent',
        isLowLoyalty && 'border-red-500/50 from-red-500/5',
        className
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Crown className="h-5 w-5 text-amber-500" />
            Party Leader
          </CardTitle>
          <Badge className="bg-amber-500 hover:bg-amber-500/80 text-amber-950 gap-1">
            <Crown className="h-3 w-3" />
            Leader
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Leader identity section */}
        <div className="flex items-start gap-4">
          {/* Portrait placeholder */}
          <div className="relative shrink-0">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center border-2 border-amber-500/50">
              <span className="text-2xl font-bold text-muted-foreground">
                {leader.name.charAt(0)}
              </span>
            </div>
            {isLowLoyalty && (
              <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5">
                <AlertTriangle className="h-3 w-3 text-white" />
              </div>
            )}
          </div>

          {/* Name and basic info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg truncate">{leader.name}</h3>
            <p className="text-sm text-muted-foreground">Age {leader.age}</p>
            <div className="flex items-center gap-2 mt-1">
              <IdeologyAlignmentBadge
                candidateIdeology={leader.ideology_vector}
                partyIdeology={partyIdeology}
              />
            </div>
          </div>
        </div>

        {/* Key stats */}
        <div className="space-y-2">
          <CandidateStatBar
            label="Loyalty"
            value={leader.loyalty}
            colorMode="positive"
          />
          <CandidateStatBar
            label="Recognition"
            value={leader.recognition}
            colorMode="positive"
          />
        </div>

        {/* Skills summary */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-1.5 text-sm">
            <Star className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-muted-foreground">Best:</span>
            <span className="font-medium">{bestSkill.name}</span>
            <span className="text-muted-foreground">({Math.round(bestSkill.value)})</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-xs"
            onClick={onViewDetails}
          >
            View Details
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>

        {/* Low loyalty warning */}
        {isLowLoyalty && (
          <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-red-700 dark:text-red-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">Leadership Crisis</p>
              <p className="text-xs mt-0.5 opacity-90">
                Your party leader has low loyalty and may defect or cause internal conflicts.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
